import { Extension, type Content } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export type AnimationStyle = 'fade' | 'slide' | 'fade-slide';

export interface InsertAnimatedOptions {
  /** Animation style. Default: 'fade-slide' */
  animation?: AnimationStyle;
  /** Animation duration in ms. Default: 300 */
  duration?: number;
  /** Delay between blocks in ms. Default: 50 */
  stagger?: number;
  /** Animate top-level blocks only ('block') or all nested blocks ('deep'). Default: 'block' */
  staggerDepth?: 'block' | 'deep';
  /** If true, typing during animation clears decorations immediately. Default: false */
  cancelOnInput?: boolean;
}

export interface AnimateInsertionsOptions {
  /** Default animation style. Default: 'fade-slide' */
  defaultAnimation?: AnimationStyle;
  /** Default duration in ms. Default: 300 */
  defaultDuration?: number;
  /** Default stagger in ms. Default: 50 */
  defaultStagger?: number;
  /** Default stagger depth. Default: 'block' */
  defaultStaggerDepth?: 'block' | 'deep';
  /** Default cancel on input behavior. Default: false */
  defaultCancelOnInput?: boolean;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    animateInsertions: {
      /** Insert content at cursor with staggered block animations */
      insertContentAnimated: (
        content: Content,
        options?: InsertAnimatedOptions
      ) => ReturnType;
      /** Replace entire document with staggered block animations */
      setContentAnimated: (
        content: Content,
        options?: InsertAnimatedOptions
      ) => ReturnType;
      /** Clear all active insertion animations */
      clearInsertAnimations: () => ReturnType;
    };
  }
}

const ANIMATION_CLASS_MAP: Record<AnimationStyle, string> = {
  fade: 'animate-insert-fade',
  slide: 'animate-insert-slide',
  'fade-slide': 'animate-insert-fade-slide',
};

interface AnimationMeta {
  ranges: Array<{ from: number; to: number }>;
  animation: AnimationStyle;
  duration: number;
  stagger: number;
  cancelOnInput: boolean;
}

interface PendingAnimationConfig {
  animation: AnimationStyle;
  duration: number;
  stagger: number;
  staggerDepth: 'block' | 'deep';
  cancelOnInput: boolean;
  mode: 'set' | 'insert';
  startPos?: number;
  /** Doc size before insertion, used to calculate inserted content bounds */
  oldDocSize?: number;
  /** Selection size before insertion, used to calculate actual inserted content size */
  selectionSize?: number;
}

type PluginMeta =
  | { type: 'animate'; meta: AnimationMeta }
  | { type: 'pending'; config: PendingAnimationConfig }
  | { type: 'clear' };

interface AnimationState {
  decorations: DecorationSet;
  cleanupTimer: ReturnType<typeof setTimeout> | null;
  cancelOnInput: boolean;
}

const pluginKey = new PluginKey<AnimationState>('animateInsertions');

function createAnimationDecorations(
  doc: Parameters<typeof DecorationSet.create>[0],
  meta: AnimationMeta
): DecorationSet {
  const decorations: Decoration[] = [];
  const { ranges, animation, duration, stagger } = meta;
  const className = ANIMATION_CLASS_MAP[animation];

  ranges.forEach((range, index) => {
    const delay = index * stagger;
    decorations.push(
      Decoration.node(range.from, range.to, {
        class: className,
        style: `--insert-duration: ${duration}ms; --insert-delay: ${delay}ms;`,
      })
    );
  });

  return DecorationSet.create(doc, decorations);
}

function calculateRanges(
  doc: Parameters<typeof DecorationSet.create>[0],
  config: PendingAnimationConfig
): Array<{ from: number; to: number }> {
  const ranges: Array<{ from: number; to: number }> = [];

  // For insert mode, calculate the end boundary of inserted content
  // Add selectionSize to account for replaced content
  const startPos = config.startPos ?? 0;
  const insertedSize =
    config.mode === 'insert' && config.oldDocSize !== undefined
      ? doc.content.size - config.oldDocSize + (config.selectionSize ?? 0)
      : 0;

  // Guard against empty inserts
  if (config.mode === 'insert' && insertedSize <= 0) {
    return ranges;
  }

  const endPos = startPos + insertedSize;

  if (config.staggerDepth === 'block') {
    doc.forEach((node, pos) => {
      if (config.mode === 'set') {
        ranges.push({ from: pos, to: pos + node.nodeSize });
      } else {
        // insert mode - include nodes that OVERLAP with [startPos, endPos]
        const nodeEnd = pos + node.nodeSize;
        if (pos < endPos && nodeEnd > startPos) {
          ranges.push({ from: pos, to: nodeEnd });
        }
      }
    });
  } else {
    doc.descendants((node, pos) => {
      if (node.isBlock) {
        if (config.mode === 'set') {
          ranges.push({ from: pos, to: pos + node.nodeSize });
        } else {
          const nodeEnd = pos + node.nodeSize;
          if (pos < endPos && nodeEnd > startPos) {
            ranges.push({ from: pos, to: nodeEnd });
          }
        }
      }
    });
  }

  return ranges;
}

export const AnimateInsertions = Extension.create<AnimateInsertionsOptions>({
  name: 'animateInsertions',

  addOptions() {
    return {
      defaultAnimation: 'fade-slide',
      defaultDuration: 300,
      defaultStagger: 50,
      defaultStaggerDepth: 'block',
      defaultCancelOnInput: false,
    };
  },

  onDestroy() {
    const state = pluginKey.getState(this.editor.state);
    if (state?.cleanupTimer) {
      clearTimeout(state.cleanupTimer);
    }
  },

  addCommands() {
    return {
      insertContentAnimated:
        (content, options = {}) =>
        ({ editor, chain }) => {
          const opts = this.options;
          const animation =
            options.animation ?? opts.defaultAnimation ?? 'fade-slide';
          const duration = options.duration ?? opts.defaultDuration ?? 300;
          const stagger = options.stagger ?? opts.defaultStagger ?? 50;
          const staggerDepth =
            options.staggerDepth ?? opts.defaultStaggerDepth ?? 'block';
          const cancelOnInput =
            options.cancelOnInput ?? opts.defaultCancelOnInput ?? false;

          const startPos = editor.state.selection.from;
          const selectionSize =
            editor.state.selection.to - editor.state.selection.from;
          const oldDocSize = editor.state.doc.content.size;

          const pendingMeta: PluginMeta = {
            type: 'pending',
            config: {
              animation,
              duration,
              stagger,
              staggerDepth,
              cancelOnInput,
              mode: 'insert',
              startPos,
              oldDocSize,
              selectionSize,
            },
          };

          return chain()
            .command(({ tr }) => {
              tr.setMeta(pluginKey, pendingMeta);
              return true;
            })
            .insertContent(content)
            .run();
        },

      setContentAnimated:
        (content, options = {}) =>
        ({ chain }) => {
          const opts = this.options;
          const animation =
            options.animation ?? opts.defaultAnimation ?? 'fade-slide';
          const duration = options.duration ?? opts.defaultDuration ?? 300;
          const stagger = options.stagger ?? opts.defaultStagger ?? 50;
          const staggerDepth =
            options.staggerDepth ?? opts.defaultStaggerDepth ?? 'block';
          const cancelOnInput =
            options.cancelOnInput ?? opts.defaultCancelOnInput ?? false;

          const pendingMeta: PluginMeta = {
            type: 'pending',
            config: {
              animation,
              duration,
              stagger,
              staggerDepth,
              cancelOnInput,
              mode: 'set',
            },
          };

          return chain()
            .command(({ tr }) => {
              tr.setMeta(pluginKey, pendingMeta);
              return true;
            })
            .setContent(content)
            .run();
        },

      clearInsertAnimations:
        () =>
        ({ editor }) => {
          editor.view.dispatch(
            editor.state.tr.setMeta(pluginKey, { type: 'clear' })
          );
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const getEditor = () => this.editor;

    return [
      new Plugin<AnimationState>({
        key: pluginKey,

        state: {
          init: () => ({
            decorations: DecorationSet.empty,
            cleanupTimer: null,
            cancelOnInput: false,
          }),

          apply: (tr, state, _oldState, newState) => {
            const meta = tr.getMeta(pluginKey) as PluginMeta | undefined;

            if (meta?.type === 'clear') {
              if (state.cleanupTimer) {
                clearTimeout(state.cleanupTimer);
              }
              return {
                decorations: DecorationSet.empty,
                cleanupTimer: null,
                cancelOnInput: false,
              };
            }

            // pending meta is handled by appendTransaction, no state change needed
            if (meta?.type === 'pending') {
              return state;
            }

            if (meta?.type === 'animate') {
              if (state.cleanupTimer) {
                clearTimeout(state.cleanupTimer);
              }

              const { ranges, duration, stagger, cancelOnInput } = meta.meta;
              const totalTime = duration + (ranges.length - 1) * stagger;

              const cleanupTimer = setTimeout(() => {
                const editor = getEditor();
                if (editor.isDestroyed) return;
                editor.view.dispatch(
                  editor.state.tr.setMeta(pluginKey, { type: 'clear' })
                );
              }, totalTime);

              return {
                decorations: createAnimationDecorations(
                  newState.doc,
                  meta.meta
                ),
                cleanupTimer,
                cancelOnInput,
              };
            }

            // Cancel on input if configured and user typed something
            if (
              state.cancelOnInput &&
              tr.docChanged &&
              !tr.getMeta('preventCancelAnimation')
            ) {
              if (state.cleanupTimer) {
                clearTimeout(state.cleanupTimer);
              }
              return {
                decorations: DecorationSet.empty,
                cleanupTimer: null,
                cancelOnInput: false,
              };
            }

            // Map decorations through document changes
            if (tr.docChanged) {
              return {
                ...state,
                decorations: state.decorations.map(tr.mapping, tr.doc),
              };
            }

            return state;
          },
        },

        appendTransaction: (
          transactions: readonly Transaction[],
          _oldState,
          newState
        ) => {
          // Look for pending animation config in any transaction
          for (const tr of transactions) {
            const meta = tr.getMeta(pluginKey) as PluginMeta | undefined;
            if (meta?.type === 'pending') {
              const { config } = meta;
              const ranges = calculateRanges(newState.doc, config);

              if (ranges.length === 0) return null;

              const animateMeta: PluginMeta = {
                type: 'animate',
                meta: {
                  ranges,
                  animation: config.animation,
                  duration: config.duration,
                  stagger: config.stagger,
                  cancelOnInput: config.cancelOnInput,
                },
              };

              return newState.tr.setMeta(pluginKey, animateMeta);
            }
          }

          return null;
        },

        props: {
          decorations: (state) => {
            return (
              pluginKey.getState(state)?.decorations ?? DecorationSet.empty
            );
          },
        },
      }),
    ];
  },
});
